import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown } from "lucide-react";
import { Logo } from "./Logo";
import { useLang } from "../i18n/LanguageContext";
import { waLink } from "../lib/site";

const navItems = (t) => [
  { to: "/", label: t.nav.home },
  { to: "/about", label: t.nav.about },
  { to: "/services", label: t.nav.services, hasMenu: true },
  { to: "/resources", label: t.nav.resources },
  { to: "/case-studies", label: t.nav.cases },
  { to: "/contact", label: t.nav.contact },
];

export const Header = () => {
  const { t, lang, toggleLang } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [svcOpen, setSvcOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const svc = [
    { key: "source", label: t.nav.servicesMenu.source },
    { key: "pay", label: t.nav.servicesMenu.pay },
    { key: "finance", label: t.nav.servicesMenu.finance },
    { key: "move", label: t.nav.servicesMenu.move },
  ];

  const goTalk = () => window.open(waLink(t.hero.waMsg), "_blank");
  const isActive = (to) => (to === "/" ? location.pathname === "/" : location.pathname.startsWith(to));

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 bg-white transition-[box-shadow,border-color] duration-300 ${
        scrolled || mobileOpen ? "border-b border-black/[0.07] shadow-[0_1px_12px_rgba(11,18,32,0.05)]" : "border-b border-transparent"
      }`}
    >
      <div className="relative mx-auto flex h-[70px] max-w-7xl items-center justify-between px-5 md:px-8">
        <Link to="/" data-testid="header-logo-link" className="relative z-10">
          <Logo />
        </Link>

        <nav className="absolute left-1/2 hidden -translate-x-1/2 items-center gap-7 lg:flex">
          {navItems(t).map((item) =>
            item.hasMenu ? (
              <div
                key={item.to}
                className="relative"
                onMouseEnter={() => setSvcOpen(true)}
                onMouseLeave={() => setSvcOpen(false)}
              >
                <Link
                  to={item.to}
                  data-testid="nav-services"
                  className={`flex items-center gap-1 py-1 text-[15px] font-medium transition-colors ${
                    isActive(item.to) ? "text-sinovar-navy" : "text-sinovar-navy/70 hover:text-sinovar-navy"
                  }`}
                >
                  {item.label}
                  <ChevronDown className="h-3.5 w-3.5 opacity-60" />
                </Link>
                {isActive(item.to) && <span className="absolute -bottom-1 left-0 h-0.5 w-6 rounded-full bg-sinovar-red" />}
                <AnimatePresence>
                  {svcOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 8 }}
                      transition={{ duration: 0.18 }}
                      className="absolute left-1/2 top-full w-48 -translate-x-1/2 pt-3"
                    >
                      <div className="rounded-xl border border-black/5 bg-white p-2 shadow-xl">
                        {svc.map((s) => (
                          <Link
                            key={s.key}
                            to={`/services#${s.key}`}
                            data-testid={`nav-services-${s.key}`}
                            className="block rounded-lg px-3 py-2 text-sm font-medium text-sinovar-navy/80 transition-colors hover:bg-sinovar-soft hover:text-sinovar-blue"
                          >
                            {s.label}
                          </Link>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <div key={item.to} className="relative">
                <Link
                  to={item.to}
                  data-testid={`nav-${item.label}`}
                  className={`py-1 text-[15px] font-medium transition-colors ${
                    isActive(item.to) ? "text-sinovar-navy" : "text-sinovar-navy/70 hover:text-sinovar-navy"
                  }`}
                >
                  {item.label}
                </Link>
                {isActive(item.to) && <span className="absolute -bottom-1 left-0 h-0.5 w-6 rounded-full bg-sinovar-red" />}
              </div>
            )
          )}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={toggleLang}
            data-testid="lang-toggle"
            className="hidden items-center rounded-full border border-black/10 text-[11px] font-semibold sm:flex overflow-hidden"
          >
            <span className={`px-2.5 py-1.5 ${lang === "en" ? "bg-sinovar-navy text-white" : "text-sinovar-navy/55"}`}>EN</span>
            <span className={`px-2.5 py-1.5 ${lang === "id" ? "bg-sinovar-navy text-white" : "text-sinovar-navy/55"}`}>ID</span>
          </button>

          <button
            onClick={goTalk}
            data-testid="header-talk-cta"
            className="hidden items-center rounded-lg bg-sinovar-blue px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#0b3c8a] md:inline-flex"
          >
            {t.nav.talk}
          </button>

          <button
            onClick={() => setMobileOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            className="relative z-10 rounded-lg p-2 text-sinovar-navy lg:hidden"
            aria-label="Menu"
          >
            {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden border-t border-black/5 bg-white lg:hidden"
          >
            <div className="flex flex-col gap-1 px-5 py-4">
              {navItems(t).map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  data-testid={`mobile-nav-${item.label}`}
                  className={`rounded-lg px-3 py-3 text-base font-medium transition-colors ${
                    isActive(item.to) ? "bg-sinovar-soft text-sinovar-blue" : "text-sinovar-navy hover:bg-sinovar-soft"
                  }`}
                >
                  {item.label}
                </Link>
              ))}
              <div className="mt-2 flex items-center justify-between border-t border-black/5 pt-3">
                <button
                  onClick={toggleLang}
                  data-testid="mobile-lang-toggle"
                  className="flex items-center overflow-hidden rounded-full border border-black/10 text-xs font-semibold"
                >
                  <span className={`px-3 py-2 ${lang === "en" ? "bg-sinovar-navy text-white" : "text-sinovar-navy/55"}`}>EN</span>
                  <span className={`px-3 py-2 ${lang === "id" ? "bg-sinovar-navy text-white" : "text-sinovar-navy/55"}`}>ID</span>
                </button>
                <button
                  onClick={goTalk}
                  data-testid="mobile-talk-cta"
                  className="inline-flex items-center rounded-lg bg-sinovar-blue px-5 py-3 text-sm font-semibold text-white"
                >
                  {t.nav.talk}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
